import Helper from '../../../models/Helper';

const URI = '/project-sessions/:id';

/**
 * @desc Helper for project sessions.
 */
class ProjectSessionsHelper extends Helper {
  constructor(api) {
    super(api, URI);
  }

  /**
   * @desc Get a specific project session from its Id.
   * @param {Number} projectSessionId Id of the project session to get.
   * @return {Promise<ProjectSessionWithDetails>} The promise handling the request.
   * @example
   * const sessionId = 5678;
   * const response = await myClient.helpers.learningActivity.projectSessions.get(sessionId);
   */
  get(projectSessionId) {
    return super.get(projectSessionId);
  }

  /**
   * @desc Upload file for specific project session.
   * @param {Number} projectSessionId Id of the corresponding session.
   * @param {File} file File to upload.
   * @param {Function} [onProgress = null] Handler of 'onprogress' event.
   * @return {Promise} A promise resolving the request handling the call.
   * @example
   * const projectSessionId = 1234;
   * const myFile = new File(['foo']);
   * const request = await myClient.helpers.learningActivity
   *                  .projectSessions.upload(projectSessionId, myFile);
   * request.execute();
   * // request.abort();
   */
  async upload(projectSessionId, file, onProgress = null) {
    const request = super.upload(file, onProgress, false);
    request.url += '/files';

    return request.setRouteParams({ id: projectSessionId }).execute();
  }

  /**
   * @desc Mark a specific project session as ready for review.
   * @param {Number} projectSessionId Id of the corresponding session.
   * @return {Promise} A promise resolving the request handling the call.
   * @example
   * const projectSessionId = 1234;
   * const request = await myClient.helpers.learningActivity
   *                  .projectSessions.markAsReadyForReview(projectSessionId);
   * request.execute();
   */
  markAsReadyForReview(projectSessionId: number) {
    const request = super.create({}, false);
    request.setRouteParam('id', `${projectSessionId}`);
    request.url += '/mark-as-ready-for-review';

    return request.execute();
  }

  /**
   * @desc Change the decision of a project session
   * @example
   * const request = await myClient
   *     .helpers
   *     .learningActivity
   *     .projectSessions.changeDecision({
   *         projectSessionId: 123,
   *         updaterId: 456,
   *         learningActivityStatus: 'passed'
   *      });
   * request.execute();
   */
  changeDecision(
    projectSessionId: number,
    params: {
      learningActivityStatus: 'failed' | 'passed';
    }
  ) {
    const request = super.create(params, false);
    request.setRouteParam('id', String(projectSessionId));
    request.url += '/change-status';
    return request.execute();
  }

  /**
   * @desc Complete a project session
   * @example
   * const request = await myClient
   *     .helpers
   *     .learningActivity
   *     .projectSessions.complete({
   *         projectSessionId: 123,
   *         comment: 'Good job',
   *         currentUserId: 1234,
   *         learningActivityStatus: 'passed',
   *         presentationVideoUrl: 'https://foo.bar',
   *      });
   * request.execute();
   */
  complete(
    projectSessionId: number,
    params: {
      comment: string;
      currentUserId: number;
      learningActivityStatus: 'failed' | 'passed';
      presentationVideoUrl: string;
    }
  ) {
    const request = super.create(params, false);
    request.setRouteParam('id', String(projectSessionId));
    request.url += '/complete';
    return request.execute();
  }
}

export default ProjectSessionsHelper;
